import { BookingServiceClient } from 'fitbeat.api-clients';
import { UserProfile, HomeWorkoutSequence } from 'fitbeat.gym-core';
import React, { FunctionComponent } from 'react';
import { translate } from 'react-i18next';
import { SafeAreaView, StyleSheet, View } from 'react-native';
import { connect } from 'react-redux';
import TypeOfWorkoutWarningPreWorkout from '../../Bookings/components/TypeOfWorkoutWarningPreWorkout';
import { shouldShowCusomiseWorkoutSettingsInBooking } from '../../Bookings/selectors';
import { getApiConfig } from '../../config';
import Theme from '../../theme';
import { AuthTokenBuilder } from '../../utils';
import {
  closeDeviceSetup,
  closeHomeWorkout,
  disableMirror,
  enableMirror,
  openSetupScreen,
  resumeWorkout,
} from '../actions';
import {
  IAppStore,
  Orientation,
} from '../reducer';
import {
  selectIsStreamingWorkout, selectIsStreamingWorkoutAndNotHost, setupScreenSelector,
  orientationSelector,
  remainingSecondsInSequenceSelector,
  userHasStartedWorkoutdSelector,
  waitingToStartWorkoutSelector,
  workoutIsReadyToStart,
} from '../selectors';
import {
  liveHeartRateSelector,
} from '../selectors/heartRateMonitorSelectors';
import {
  enableMirrorSetupSelector,
  IShouldShowOnWorkout, IsPastWarmdownSelector, shouldShowElementsSelector, workoutVideoErrorSelector,
} from '../selectors/homeWorkoutSelectors';
import { IAudioHelper } from '../utils';
import { DeviceSetupScreens } from '../utils/DeviceSetupScreens';
import { DeviceSetupScreen } from './DeviceSetup/DeviceSetupScreen';
import { PreWorkoutSetup } from './DeviceSetup/PreWorkoutSetup';
import { HomeWorkoutHeartRateSetup } from './HomeWorkoutHeartRateSetup';
import { InstructorSetup } from './InstructorSetup';
import { LeaderboardSetup } from './LeaderboardSetup';
import { MirroringSetup } from './MirroringSetup';
import { MusicSetup } from './Music/MusicSetup';
import { MyPersonaliseWorkoutSetup } from './MyPersonaliseWorkoutSetup';
import { ScreenCastingSetup } from './ScreenCastingSetup';
import {
  callDisconnectRequest,
  initiateGroupCall,
  toggleLocalAudioMuted,
  toggleLocalVideoMuted,
} from './VideoCalling/actions';
import {
  isGroupWorkoutCallConnectedSelector,
  isGroupWorkoutCallConnectingSelector,
} from './VideoCalling/selectors';

export interface IDeviceSetupProps {
  userProfile: UserProfile | null;
  currentSetupScreen: DeviceSetupScreens | null;
  audioHelper: IAudioHelper;
  orientation: Orientation;
  userHasStartedWorkout: boolean;
  preloadingComplete: boolean;
  homeWorkoutVideoError: string | null;
  homeWorkoutLoading: boolean;
  isGroupWorkout: boolean;
  homeWorkoutLoadingError: string | null;
  mirrorIsActive: boolean;
  liveHeartRate: number | null;
  localAudioIsMuted: boolean;
  localVideoIsMuted: boolean;
  isGroupWorkoutCallConnected: boolean;
  isGroupWorkoutCallConnecting: boolean;
  homeWorkoutSession: HomeWorkoutSequence | null;
  landscape?: boolean;
  isLeaderboardActivated: boolean;
  waitingToStartWorkout: boolean;
  remainingSecondsInSequence: number | null;
  groupWorkoutTalkingEnabled: boolean;
  leaveWorkout: () => void;
  closeDeviceSetup: () => void;
  openSetupScreen: (screen: DeviceSetupScreens) =>void;
  t: (key: string, options?: any) => string;
  leaveWorkoutClicked: () => void;
  enableMirror: () => void;
  disableMirror: () => void;
  resumeWorkoutPressed: () => void;
  toggleLocalAudio: () => void;
  toggleLocalVideoMuted: () => void;
  disconnectGroupWorkoutCall: () => void;
  initiateGroupWorkoutCall: (callId: string) => void;
  isPastWarmdown: boolean;
  showWorkoutSummaryDeviceSetupScreen: boolean;
  isStreamingWorkout: boolean | undefined;
  isStreamingWorkoutAndNotHost: boolean;
  customiseWorkoutSettingsDisabled: boolean;
  enableInstructorChange: boolean | undefined;
  currentSequence: any;
  enableMirrorSetup: boolean;
  shouldShowElements: IShouldShowOnWorkout;
}

const SetupScreenComponent = (props: Partial<IDeviceSetupProps>) => ({
  [DeviceSetupScreens.ScreenCasting]: <ScreenCastingSetup />,
  [DeviceSetupScreens.Instructor]: <InstructorSetup />,
  [DeviceSetupScreens.HeartRate]: <HomeWorkoutHeartRateSetup {...props} />,
  [DeviceSetupScreens.MyWorkout]: <MyPersonaliseWorkoutSetup />,
  [DeviceSetupScreens.ScreenMirror]: <MirroringSetup />,
  [DeviceSetupScreens.Music]: <MusicSetup />,
  [DeviceSetupScreens.LeaderboardSetup]: <LeaderboardSetup />,
  [DeviceSetupScreens.WorkoutTypeWarning]: <TypeOfWorkoutWarningPreWorkout />,
});

export const DeviceSetupComponent: FunctionComponent<IDeviceSetupProps> = ({
  currentSetupScreen,
  userProfile,
  orientation,
  showWorkoutSummaryDeviceSetupScreen,
  currentSequence,
  ...rest
}: IDeviceSetupProps) => {
  let SetupScreen;
  if (currentSetupScreen) {
    SetupScreen = SetupScreenComponent({ userProfile })[currentSetupScreen];
  }

  const deviceSetupScreen = showWorkoutSummaryDeviceSetupScreen
    ? <PreWorkoutSetup userProfile={userProfile} {...rest} />
    : (
      <DeviceSetupScreen
        landscape={orientation === 'LANDSCAPE'}
        userProfile={userProfile}
        currentSequence={currentSequence}
        {...rest}
      />
    );

  return (
    <View style={styles.container}>
      {
        currentSetupScreen !== null
        && (
        <SafeAreaView style={styles.container}>
          {SetupScreen}
        </SafeAreaView>
        )
      }
      {
        !currentSetupScreen && deviceSetupScreen
      }
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Theme.BLACK,
  },
});

const mapStateToProps = (state: IAppStore): Partial<IDeviceSetupProps> => ({
  currentSetupScreen: setupScreenSelector(state),
  userProfile: state.homeWorkouts.userProfile,
  orientation: orientationSelector(state),
  userHasStartedWorkout: userHasStartedWorkoutdSelector(state),
  isGroupWorkout: state.homeWorkouts.isGroupWorkout,
  homeWorkoutLoading: state.homeWorkouts.homeWorkoutLoading,
  preloadingComplete: workoutIsReadyToStart(state),
  homeWorkoutVideoError: workoutVideoErrorSelector(state),
  homeWorkoutLoadingError: state.homeWorkouts.homeWorkoutLoadingError,
  mirrorIsActive: state.homeWorkouts.mirrorIsActive,
  liveHeartRate: liveHeartRateSelector(state),
  localAudioIsMuted: state.groupCalling.localAudioIsMuted,
  localVideoIsMuted: state.groupCalling.localVideoIsMuted,
  homeWorkoutSession: state.homeWorkouts.homeWorkoutSession,
  waitingToStartWorkout: waitingToStartWorkoutSelector(state),
  remainingSecondsInSequence: remainingSecondsInSequenceSelector(state),
  groupWorkoutTalkingEnabled: state.groupCalling.talkingEnabled,
  isLeaderboardActivated: state.homeWorkouts.isLeaderboardActivated,
  showWorkoutSummaryDeviceSetupScreen: state.homeWorkouts.showWorkoutSummaryDeviceSetupScreen,
  isGroupWorkoutCallConnecting: isGroupWorkoutCallConnectingSelector(state),
  isGroupWorkoutCallConnected: isGroupWorkoutCallConnectedSelector(state),
  isPastWarmdown: IsPastWarmdownSelector(state),
  isStreamingWorkout: selectIsStreamingWorkout(state),
  isStreamingWorkoutAndNotHost: selectIsStreamingWorkoutAndNotHost(state),
  customiseWorkoutSettingsDisabled: !shouldShowCusomiseWorkoutSettingsInBooking(
    state.homeWorkouts.homeWorkoutSession,
  ),
  enableInstructorChange: state.homeWorkouts?.homeWorkoutSession?.enableInstructorChange,
  shouldShowElements: shouldShowElementsSelector(state) as IShouldShowOnWorkout,
  enableMirrorSetup: enableMirrorSetupSelector(state),
});

const mapDispatchToProps = (dispatch: any, ownProps: IDeviceSetupProps): Partial<IDeviceSetupProps> => {
  const bookingServiceClient = new BookingServiceClient(
    getApiConfig(),
    fetch,
    new AuthTokenBuilder(),
  );

  return {
    openSetupScreen: (screen: DeviceSetupScreens) => dispatch(openSetupScreen(screen)),
    closeDeviceSetup: () => dispatch(closeDeviceSetup()),
    enableMirror: () => dispatch(enableMirror()),
    disableMirror: () => dispatch(disableMirror()),
    leaveWorkout: () => dispatch(closeHomeWorkout()),
    resumeWorkoutPressed: () => dispatch(resumeWorkout(bookingServiceClient, ownProps.audioHelper)),
    toggleLocalAudio: () => dispatch(toggleLocalAudioMuted()),
    toggleLocalVideoMuted: () => dispatch(toggleLocalVideoMuted()),
    disconnectGroupWorkoutCall: () => dispatch(callDisconnectRequest()),
    initiateGroupWorkoutCall: (callId) => dispatch(
      initiateGroupCall(bookingServiceClient, ownProps.t, ownProps.userProfile!, callId),
    ),
  };
};

export const DeviceSetup = connect(mapStateToProps, mapDispatchToProps)(
  translate(['homeWorkouts'])(DeviceSetupComponent),
);
